/**
 * The columns panel: visibility, order and pinning.
 *
 * Reordering is drag-and-drop using the HTML5 drag events rather than a
 * library — a vertical list of a dozen rows is the one case where the native
 * API is enough, and a drag-and-drop dependency in a shared package lands in
 * every consumer's bundle for this one panel.
 *
 * Keyboard controls sit beside the drag handle for the same reason the focus
 * ring exists: a rearrangement that can only be done by dragging cannot be done
 * at all by some people.
 */

import { useState } from 'react';
import { ArrowDown, ArrowUp, Eye, EyeOff, GripVertical, Pin, PinOff, RotateCcw } from 'lucide-react';

import { cn } from '../../lib/cn';
import { Button } from '../ui/button';
import type { PinSide } from './use-table-state';
import { ScrollArea } from '../ui/scroll-area';

export interface ColumnsPanelColumn {
  field: string;
  header: string;
}

export function ColumnsPanel({
  columns,
  hidden,
  pins,
  onToggleVisible,
  onMove,
  onTogglePin,
  onReset,
  onClose,
}: {
  /** In their current display order. */
  columns: readonly ColumnsPanelColumn[];
  hidden: readonly string[];
  pins: Record<string, PinSide>;
  onToggleVisible: (field: string) => void;
  onMove: (field: string, toIndex: number) => void;
  onTogglePin: (field: string) => void;
  onReset: () => void;
  onClose: () => void;
}) {
  const [dragging, setDragging] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);

  const visibleCount = columns.length - hidden.length;

  return (
    <div className="w-72 p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-xs font-semibold text-heading">Columns</p>
        <span className="text-2xs text-faint tabular">
          {visibleCount} of {columns.length} shown
        </span>
      </div>

      <ScrollArea className="max-h-72" contentClassName="space-y-px">
        {columns.map((column, index) => {
          const isHidden = hidden.includes(column.field);
          const pin = pins[column.field];
          const isDragTarget = dragOver === index && dragging !== column.field;

          return (
            <div
              key={column.field}
              draggable
              onDragStart={() => setDragging(column.field)}
              onDragEnd={() => {
                setDragging(null);
                setDragOver(null);
              }}
              onDragOver={(event) => {
                // Without preventDefault the drop never fires — the browser's
                // default is to refuse the drop.
                event.preventDefault();
                setDragOver(index);
              }}
              onDrop={(event) => {
                event.preventDefault();
                if (dragging) onMove(dragging, index);
                setDragging(null);
                setDragOver(null);
              }}
              className={cn(
                'group flex items-center gap-1.5 rounded px-1 py-1 transition-colors',
                dragging === column.field && 'opacity-40',
                isDragTarget && 'bg-accent-subtle',
              )}
            >
              <GripVertical
                className="size-3.5 shrink-0 cursor-grab text-faint"
                aria-hidden
              />

              <button
                type="button"
                onClick={() => onToggleVisible(column.field)}
                className="flex min-w-0 flex-1 items-center gap-2 text-left"
                aria-pressed={!isHidden}
              >
                {isHidden ? (
                  <EyeOff className="size-3.5 shrink-0 text-faint" aria-hidden />
                ) : (
                  <Eye className="size-3.5 shrink-0 text-accent" aria-hidden />
                )}
                <span
                  className={cn(
                    'truncate text-xs',
                    isHidden ? 'text-faint line-through' : 'text-body',
                  )}
                >
                  {column.header}
                </span>
              </button>

              {/* Keyboard equivalents for the drag. Revealed on hover and focus
                  so the row is not a wall of buttons, but reachable by tab. */}
              <div className="flex shrink-0 items-center opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
                <button
                  type="button"
                  disabled={index === 0}
                  onClick={() => onMove(column.field, index - 1)}
                  className="rounded p-0.5 text-faint hover:text-body disabled:opacity-30"
                  aria-label={`Move ${column.header} up`}
                >
                  <ArrowUp className="size-3" />
                </button>
                <button
                  type="button"
                  disabled={index === columns.length - 1}
                  onClick={() => onMove(column.field, index + 1)}
                  className="rounded p-0.5 text-faint hover:text-body disabled:opacity-30"
                  aria-label={`Move ${column.header} down`}
                >
                  <ArrowDown className="size-3" />
                </button>
              </div>

              <button
                type="button"
                onClick={() => onTogglePin(column.field)}
                className={cn(
                  'shrink-0 rounded p-0.5',
                  pin ? 'text-accent' : 'text-faint opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 hover:text-body',
                )}
                aria-label={pin ? `Unpin ${column.header}` : `Pin ${column.header} left`}
                title={pin ? 'Unpin' : 'Pin to the left'}
              >
                {pin ? <Pin className="size-3" /> : <PinOff className="size-3" />}
              </button>
            </div>
          );
        })}
      </ScrollArea>

      <div className="mt-2.5 flex items-center justify-between gap-2 border-t border-line pt-2.5">
        <Button variant="ghost" size="sm" onClick={onReset} className="text-xs text-muted">
          <RotateCcw className="size-3.5" />
          Reset
        </Button>
        <Button variant="secondary" size="sm" onClick={onClose}>
          Done
        </Button>
      </div>
    </div>
  );
}
